"use client";

import { useRef, useState } from "react";

const PJK = "'Plus Jakarta Sans', var(--font-plus-jakarta), sans-serif";

const MAX_FILES = 8;
const MAX_BYTES = 25 * 1024 * 1024; // 25 MB

export interface AttachmentFile {
  filename: string;
  mimeType: string;
  contentBase64: string;
}

interface InputBarProps {
  placeholder?: string;
  onSubmit?: (text: string) => void;
  disabled?: boolean;
  onChangeText?: (value: string) => void;
  /** Called whenever the selected attachments change — stash in context. */
  onAttachmentsChange?: (files: AttachmentFile[]) => void;
}

export default function InputBar({
  placeholder = "Type a task or question...",
  onSubmit,
  disabled = false,
  onChangeText,
  onAttachmentsChange,
}: InputBarProps) {
  const [value, setValue] = useState("");
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const updateValue = (next: string) => {
    setValue(next);
    onChangeText?.(next);
  };

  const updateAttachments = (next: AttachmentFile[]) => {
    setAttachments(next);
    onAttachmentsChange?.(next);
  };

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    onSubmit?.(trimmed);
    updateValue("");
    if (inputRef.current) inputRef.current.style.height = "auto";
    // Clear attachments after submit
    updateAttachments([]);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    // Reset the input so the same file can be re-added after removal
    e.target.value = "";

    const remaining = MAX_FILES - attachments.length;
    const toProcess = files.slice(0, remaining);

    toProcess.forEach((file) => {
      if (file.size > MAX_BYTES) {
        // Silently skip oversized files (could show toast in a future pass)
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        // Strip the "data:<mime>;base64," prefix
        const base64 = dataUrl.split(",")[1] ?? "";
        const attachment: AttachmentFile = {
          filename: file.name,
          mimeType: file.type || "application/octet-stream",
          contentBase64: base64,
        };
        setAttachments((prev) => {
          if (prev.length >= MAX_FILES) return prev;
          const next = [...prev, attachment];
          onAttachmentsChange?.(next);
          return next;
        });
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAttachment = (idx: number) => {
    const next = attachments.filter((_, i) => i !== idx);
    updateAttachments(next);
  };

  const hasText = value.trim().length > 0;

  return (
    <>
    <style>{`.botwork-inputbar-textarea::placeholder{color:rgba(205,194,216,0.5);}`}</style>
    <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 6 }}>
      {/* Attachment chips */}
      {attachments.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
            padding: "2px 0",
          }}
        >
          {attachments.map((att, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "rgba(134,53,246,0.12)",
                border: "1px solid rgba(134,53,246,0.35)",
                borderRadius: 9999,
                padding: "4px 10px 4px 12px",
                maxWidth: 220,
              }}
            >
              <span
                style={{
                  fontFamily: PJK,
                  fontSize: 12,
                  fontWeight: 500,
                  color: "#CDC2D8",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: 160,
                }}
              >
                {att.filename}
              </span>
              <button
                aria-label={`Remove ${att.filename}`}
                onClick={() => removeAttachment(idx)}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  lineHeight: 1,
                  color: "rgba(205,194,216,0.6)",
                  fontSize: 14,
                  flexShrink: 0,
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div
        className="app-inputbar"
        style={{
          width: "100%",
          maxWidth: 720,
          borderRadius: 20,
          background: "rgba(229,226,225,0.06)",
          border: "1px solid #353535",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          display: "flex",
          alignItems: "center",
          paddingLeft: 9,
          paddingRight: 13,
          paddingTop: 9,
          paddingBottom: 9,
          gap: 12,
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? "none" : "auto",
        }}
      >
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="*/*"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        {/* Plus button */}
        <button
          aria-label="Add attachment"
          className="app-plus-btn"
          onClick={() => fileInputRef.current?.click()}
          style={{
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <img
            src="/app-icons/plus.svg"
            alt=""
            style={{ width: 24, height: 24 }}
          />
        </button>

        {/* Textarea */}
        <div style={{ flex: 1, minHeight: 44, maxHeight: 160 }}>
          <textarea
            ref={inputRef}
            value={value}
            onChange={(e) => updateValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            onInput={(e) => {
              const t = e.target as HTMLTextAreaElement;
              t.style.height = "auto";
              t.style.height = Math.min(t.scrollHeight, 160) + "px";
            }}
            placeholder={placeholder}
            rows={1}
            className="botwork-inputbar-textarea"
            style={{
              width: "100%",
              background: "transparent",
              border: "none",
              outline: "none",
              resize: "none",
              fontFamily: PJK,
              fontWeight: 400,
              fontSize: 16,
              lineHeight: "26px",
              color: "#F2F0ED",
              padding: "10px 12px",
              maxHeight: 160,
              caretColor: "#8635F6",
            }}
          />
        </div>

        {/* Send button */}
        <button
          onClick={handleSubmit}
          aria-label="Send"
          className="app-send-btn"
          disabled={!hasText}
          style={{
            width: 40,
            height: 40,
            borderRadius: 14,
            background: "#8635F6",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "none",
            cursor: hasText ? "pointer" : "default",
            flexShrink: 0,
            opacity: hasText ? 1 : 0.6,
            transition: "opacity 150ms ease-out",
          }}
        >
          <img
            src="/app-icons/location-arrow.svg"
            alt=""
            style={{ width: 18, height: 18 }}
          />
        </button>
      </div>
    </div>
    </>
  );
}
